import { useState } from "react";
import { Share2, Copy, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

const PHONE = "447426848476";

/**
 * Build a shareable URL for the current configurator state.
 * Each configurator page hydrates these params back into config on mount.
 */
function buildShareUrl(variant, config) {
  const path = variant === "wood" ? "/configurator" : variant === "pvc" ? "/pvc" : "/engineered";
  const params = new URLSearchParams();

  if (variant === "engineered") {
    if (config.size_key) params.set("size", config.size_key);
    if (config.glass_thickness != null) params.set("glass", String(config.glass_thickness));
  } else {
    if (config.shape) params.set("shape", config.shape);
    if (config.width != null) params.set("w", String(config.width));
    if (config.height != null) params.set("h", String(config.height));
    if (config.depth != null) params.set("d", String(config.depth));
    if (config.glass_thickness != null) params.set("glass", String(config.glass_thickness));
    if (config.stain_color) params.set("stain", config.stain_color);
    if (config.pvc_color) params.set("pvc", config.pvc_color);
  }

  return `${window.location.origin}${path}?${params.toString()}`;
}

const ShareConfigButton = ({ variant = "wood", config, summary = "my custom enclosure" }) => {
  const [copied, setCopied] = useState(false);
  const url = buildShareUrl(variant, config);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — paste it anywhere");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Long-press to copy manually.");
    }
  };

  const waMessage = `Hi! Take a look at ${summary} I just configured: ${url}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(waMessage)}`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 bg-white/5 hover:bg-white/10 text-[#F4F1DE] text-xs font-semibold uppercase tracking-wider transition-colors"
          data-testid="share-config-button"
        >
          <Share2 className="w-3.5 h-3.5" />
          Share this build
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 bg-[#0A0A0A] border-white/10 text-[#F4F1DE]"
        data-testid="share-config-popover"
      >
        <div className="space-y-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#F4F1DE]/50 mb-1.5">
              Share with anyone (e.g. your partner)
            </p>
            <div className="flex items-stretch gap-1">
              <input
                readOnly
                value={url}
                className="flex-1 bg-black/40 border border-white/10 px-2 py-1.5 text-xs text-[#F4F1DE]/80 truncate"
                data-testid="share-config-url-input"
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                onClick={copyLink}
                className="px-2.5 bg-[#E07A5F] hover:bg-[#D06A4F] text-white text-xs font-semibold flex items-center gap-1"
                data-testid="share-config-copy"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1eba56] text-white text-sm font-semibold py-2 transition-colors"
            data-testid="share-config-whatsapp"
          >
            <MessageCircle className="w-4 h-4" />
            Send via WhatsApp
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ShareConfigButton;
